(() => {
  'use strict';
  if (customElements.get('otf-lazy-video')) return;
  class OtfLazyVideo extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.initialized = true;
      this.toggle = this.querySelector('[data-lazy-video-toggle]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayRequested = this.video.dataset.otfAutoplay === 'true';
      this.loaded = this.video.dataset.otfLoaded === 'true';
      this.nearViewport = false;
      this.inViewport = false;
      this.userPaused = false;

      this.onPlay = () => this.syncControl();
      this.onPause = () => {
        if (this.inViewport && !document.hidden && !this.reducedMotion.matches) this.userPaused = true;
        this.syncControl();
      };
      this.onEnded = () => { this.userPaused = true; this.syncControl(); };
      this.onToggle = () => {
        if (this.video.paused) {

          this.userPaused = false;
          this.loadSources();
          this.play();
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      };
      this.onMotionChange = () => {
        if (this.reducedMotion.matches) this.video.pause();
        else this.updatePlayback();
        this.syncControl();
      };
      this.onVisibilityChange = () => this.updatePlayback();
      this.video.addEventListener('play', this.onPlay);
      this.video.addEventListener('pause', this.onPause);
      this.video.addEventListener('ended', this.onEnded);
      this.reducedMotion.addEventListener('change', this.onMotionChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', this.onToggle);
      }
      if ('IntersectionObserver' in window) {
        this.loadObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            this.nearViewport = entry.isIntersecting;
            this.updatePlayback();
          });
        }, { rootMargin: '300px 0px' });
        this.playObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            this.inViewport = entry.isIntersecting;
            this.updatePlayback();
          });
        }, { threshold: 0.05 });
        this.loadObserver.observe(this.video);
        this.playObserver.observe(this.video);
      } else {
        this.nearViewport = true;
        this.inViewport = true;
        this.updatePlayback();
      }
      this.syncControl();
    }
    loadSources() {
      if (this.loaded) return;
      this.video.querySelectorAll('source[data-src]').forEach(source => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      this.loaded = true;
      this.video.dataset.otfLoaded = 'true';
      this.video.load();
    }
    updatePlayback() {
      if (this.nearViewport) this.loadSources();
      if (!this.inViewport || document.hidden) {
        this.video.pause();
        return;
      }
      if (this.loaded && this.autoplayRequested && !this.userPaused && !this.reducedMotion.matches) this.play();
    }
    play() {
      try {
        const pending = this.video.play();
        if (pending && typeof pending.catch === 'function') pending.catch(() => this.syncControl());
      } catch (_) {
        this.syncControl();
      }
    }
    syncControl() {
      if (!this.toggle) return;
      const paused = this.video.paused;
      this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
      this.toggle.setAttribute('aria-pressed', String(!paused));
      const icon = this.toggle.querySelector('[data-lazy-video-icon]');
      if (icon) icon.textContent = paused ? '▶' : 'Ⅱ';
    }
    disconnectedCallback() {
      if (!this.initialized) return;
      this.inViewport = false;
      this.loadObserver?.disconnect();
      this.playObserver?.disconnect();
      this.reducedMotion.removeEventListener('change', this.onMotionChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.toggle?.removeEventListener('click', this.onToggle);
      this.video.removeEventListener('play', this.onPlay);
      this.video.removeEventListener('pause', this.onPause);
      this.video.removeEventListener('ended', this.onEnded);
      this.video.pause();
      this.initialized = false;
    }
  }
  customElements.define('otf-lazy-video', OtfLazyVideo);
})();
